import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'

import { BehaviorSubject, firstValueFrom } from 'rxjs'

import { environment } from '../../../environments/environment'
import { SituacaoCarregamento } from '../../features/carregamentos/enums/situacao-carregamento.enum'
import { ChaveValor } from '../../shared/library/interface/chave-valor.interface'
import { APIResponse } from '../interfaces/api-response.interface'

type SettingsCache = Record<string, any>
interface SettingItem {
  id: number
  namespace: string
  setting_key: string
  scope: string
  user_id: number | null
  setting_value: any
  description?: string
  enabled?: boolean
  created_at?: string
  updated_at?: string
}
interface SettingsList {
  items: SettingItem[]
  count: number
}

@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private http = inject(HttpClient)
  private baseUrl = `${environment.apiBase}settings`
  private cache$ = new BehaviorSubject<SettingsCache>({})
  private loaded = false
  private loadingPromise: Promise<void> | null = null

  init(): Promise<void> {
    return this.fetchAll(this.baseUrl).catch((err) => {
      console.error('[AppSettings] init error', err)
    })
  }

  reload(): Promise<void> {
    return this.fetchAll(this.baseUrl).catch((err) => {
      console.error('[AppSettings] reload error', err)
    })
  }

  getNumber(key: string): number {
    this.ensureLoadedSync()
    const value = this.cache$.value[key]
    if (value === undefined) return 0
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      const n = Number(value)
      if (Number.isFinite(n)) return n
    }
    console.warn('[AppSettings] getNumber type mismatch', { key, value })
    throw new TypeError(`Setting ${key} is not a number`)
  }

  getString(key: string): string {
    this.ensureLoadedSync()
    const value = this.cache$.value[key]
    if (value === undefined) return ''
    if (typeof value === 'string') return value
    console.warn('[AppSettings] getString type mismatch', { key, value })
    throw new TypeError(`Setting ${key} is not a string`)
  }

  getBoolean(key: string): boolean {
    this.ensureLoadedSync()
    const value = this.cache$.value[key]
    if (value === undefined) return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase()
      if (v === 'true' || v === '1') return true
      if (v === 'false' || v === '0') return false
    }
    if (typeof value === 'number') {
      if (value === 1) return true
      if (value === 0) return false
    }
    console.warn('[AppSettings] getBoolean type mismatch', { key, value })
    throw new TypeError(`Setting ${key} is not a boolean`)
  }

  getArray<T = any>(key: string): T[] {
    this.ensureLoadedSync()
    const value = this.cache$.value[key]
    if (value === undefined) return [] as T[]
    if (Array.isArray(value)) return value as T[]
    console.warn('[AppSettings] getArray type mismatch', { key, value })
    throw new TypeError(`Setting ${key} is not an array`)
  }

  getObject<T extends Record<string, any> = Record<string, any>>(key: string): T {
    this.ensureLoadedSync()
    const value = this.cache$.value[key]
    if (value === undefined) return {} as T
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) return value as T
    console.warn('[AppSettings] getObject type mismatch', { key, value })
    throw new TypeError(`Setting ${key} is not an object`)
  }

  getAllSettings(): Record<string, any> {
    this.ensureLoadedSync()
    return { ...this.cache$.value }
  }

  getCarregamentoSituacoes(): ChaveValor[] {
    return Object.values(SituacaoCarregamento).map((v) => ({ chave: v, valor: v }))
  }

  getCarregamentoReusos(): ChaveValor[] {
    const base: ChaveValor[] = [
      { chave: null, valor: '' },
      { chave: 'SIM', valor: 'Sim' },
      { chave: 'NAO', valor: 'Não' },
    ]
    return base
  }

  getCarregamentoTipos(): ChaveValor[] {
    return [
      { chave: 'COLETA', valor: 'Coleta' },
      { chave: 'ENTREGA', valor: 'Entrega' },
    ]
  }

  getCarregamentoTiposBusca(): ChaveValor[] {
    return [
      { chave: null, valor: 'Coleta/Entrega' },
      { chave: 'COLETA', valor: 'Coleta' },
      { chave: 'ENTREGA', valor: 'Entrega' },
    ]
  }

  getAppVersion(): string {
    try {
      const v = environment.version as string
      if (typeof v === 'string' && v.length > 0) return v
      return 'unknown'
    } catch {
      return 'unknown'
    }
  }

  getEnvironmentName(): 'production' | 'development' {
    return environment.production ? 'production' : 'development'
  }

  getVersionLabel(): string {
    const v = this.getAppVersion()
    const envName = this.getEnvironmentName()
    return envName === 'production' ? v : `${v} (dev)`
  }

  private ensureLoadedSync(): void {
    if (!this.loaded) {
      // no-op when settings not yet loaded
    }
  }

  private async fetchAll(url: string): Promise<void> {
    try {
      const resp = await firstValueFrom(this.http.get<APIResponse<SettingsList>>(url))
      const items = (resp?.data as any)?.items ?? []
      const next: SettingsCache = {}
      for (const it of items as SettingItem[]) next[it.setting_key] = it.setting_value
      this.cache$.next(next)
      console.log('cache', this.cache$.value)
      this.loaded = true
      this.baseUrl = url
    } catch (err: any) {
      const status = err?.status ?? err?.error?.status
      const message = err?.error?.message ?? err?.message
      // Fallback: mark as loaded with empty cache to avoid blocking app
      this.cache$.next({})
      this.loaded = true
    }
  }

  private requireKey(key: string): any {
    const cached = this.cache$.value[key]
    if (cached === undefined) {
      return undefined
    }
    return cached
  }
}
